import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Pill } from "../ui";
import { useAuth } from "../../context/AuthContext";
import {
  ensureFelixWelcomeThread,
  ensureMessageThread,
  listenMessageThreads,
  listenThreadMessages,
  sendMessage,
} from "../../services/messageRepository";

const OPEN_MESSAGES_EVENT = "denkup:openMessages";

export function openMessageDock(targetProfile = null) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_MESSAGES_EVENT, { detail: { targetProfile } }));
}

function avatarLabel(profile = {}) {
  return profile.initials || String(profile.displayName || profile.nickname || "DU")
    .trim()
    .split(/\s+/g)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toLocaleUpperCase("tr-TR"))
    .join("");
}

function profileAvatar(profile = {}, className = "") {
  const imageUrl = profile.profileImage?.url || profile.avatarUrl || "";
  return (
    <span className={`message-avatar ${className}`.trim()}>
      {imageUrl ? <img src={imageUrl} alt="" /> : <span>{avatarLabel(profile)}</span>}
    </span>
  );
}

function timeLabel(ms) {
  if (!ms) return "";
  return new Intl.DateTimeFormat("tr-TR", { hour: "2-digit", minute: "2-digit" }).format(new Date(ms));
}

function messageErrorText(error, fallback) {
  const message = String(error?.message || "");
  if (message.includes("requires an index")) return "Mesaj konuşmaları için gerekli Firestore index henüz hazır değil. Liste geçici olarak sade sorguyla yüklenir.";
  if (message.includes("missing or insufficient permissions")) return "Mesaj gönderme izni reddedildi. Konuşma katılımcısı, arkadaşlık veya mesaj alma ayarlarını kontrol et.";
  return message || fallback;
}

export default function MessageDock({ onNavigate }) {
  const { user, profile } = useAuth();
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [selectedThreadId, setSelectedThreadId] = useState("");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [status, setStatus] = useState({ type: "", text: "" });
  const [loadingTarget, setLoadingTarget] = useState(false);
  const messagesEndRef = useRef(null);
  const welcomeSeededRef = useRef("");

  const selectedThread = useMemo(
    () => threads.find((thread) => thread.id === selectedThreadId) || null,
    [selectedThreadId, threads],
  );
  const recentThreads = threads.slice(0, 3);

  useEffect(() => {
    if (!user?.uid) {
      setThreads([]);
      setSelectedThreadId("");
      setMessages([]);
      return undefined;
    }
    return listenMessageThreads(user, (rows) => {
      setThreads(rows);
      setStatus((current) => (current.type === "error" ? current : { type: "", text: "" }));
      setSelectedThreadId((current) => current || rows[0]?.id || "");
    }, (error) => {
      setStatus({ type: "error", text: messageErrorText(error, "Mesaj konuşmaları alınamadı.") });
    });
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || welcomeSeededRef.current === user.uid) return;
    welcomeSeededRef.current = user.uid;
    ensureFelixWelcomeThread({ user, currentProfile: profile }).catch((error) => {
      setStatus((current) => current.text ? current : { type: "error", text: messageErrorText(error, "Felix karşılama mesajı hazırlanamadı.") });
    });
  }, [profile, user]);

  useEffect(() => {
    if (!selectedThreadId) {
      setMessages([]);
      return undefined;
    }
    return listenThreadMessages(selectedThreadId, (rows) => {
      setMessages(rows);
      window.setTimeout(() => messagesEndRef.current?.scrollIntoView?.({ block: "end" }), 20);
    }, (error) => {
      setStatus({ type: "error", text: messageErrorText(error, "Mesajlar alınamadı.") });
    });
  }, [selectedThreadId]);

  useEffect(() => {
    const handleOpen = async (event) => {
      if (!user?.uid) {
        setOpen(false);
        onNavigate?.("/giris");
        return;
      }
      const targetProfile = event.detail?.targetProfile;
      setOpen(true);
      if (!targetProfile?.uid) return;
      setLoadingTarget(true);
      setStatus({ type: "", text: "" });
      try {
        const thread = await ensureMessageThread({ user, currentProfile: profile, targetProfile });
        setSelectedThreadId(thread.id);
      } catch (error) {
        setStatus({ type: "error", text: messageErrorText(error, "Mesaj penceresi açılamadı.") });
      } finally {
        setLoadingTarget(false);
      }
    };
    window.addEventListener(OPEN_MESSAGES_EVENT, handleOpen);
    return () => window.removeEventListener(OPEN_MESSAGES_EVENT, handleOpen);
  }, [onNavigate, profile, user]);

  const submitMessage = async (event) => {
    event.preventDefault();
    if (!selectedThread) return;
    setStatus({ type: "", text: "" });
    try {
      await sendMessage({ user, thread: selectedThread, body: draft });
      setDraft("");
    } catch (error) {
      setStatus({ type: "error", text: messageErrorText(error, "Mesaj gönderilemedi.") });
    }
  };

  if (!user?.uid) return null;

  return (
    <div className={`message-dock ${open ? "is-open" : "is-collapsed"}`}>
      {!open ? (
        <button className="message-float-card" type="button" onClick={() => setOpen(true)} aria-label="Mesajları aç">
          <span className="message-float-avatars">
            {recentThreads.map((thread, index) => (
              <span className={`message-float-avatar message-float-avatar--${index + 1}`} key={thread.id}>
                {thread.other?.profileImage?.url ? <img src={thread.other.profileImage.url} alt="" /> : <span>{avatarLabel(thread.other)}</span>}
              </span>
            ))}
          </span>
          <span className="message-float-pulse" />
        </button>
      ) : null}

      {open ? (
        <section className="message-window" aria-label="Mesajlar">
          <header className="message-window-head">
            <div>
              <Pill tone="green">Mesajlar</Pill>
              <h2>Kullanıcı Mesajları</h2>
            </div>
            <button type="button" className="message-window-close" onClick={() => setOpen(false)} aria-label="Mesajları kapat">×</button>
          </header>

          <div className="message-window-grid">
            <aside className="message-thread-list" aria-label="Konuşmalar">
              {threads.length ? threads.map((thread) => (
                <button
                  className={`message-thread-item ${thread.id === selectedThreadId ? "is-active" : ""}`}
                  type="button"
                  key={thread.id}
                  onClick={() => setSelectedThreadId(thread.id)}
                >
                    {profileAvatar(thread.other)}
                    <span>
                      <strong>{thread.other.displayName || "Kullanıcı"}</strong>
                    <small>{thread.other.profileTitle || (thread.other.level ? `Level ${thread.other.level}` : thread.lastMessagePreview || "Konuşma başlatıldı.")}</small>
                    </span>
                  <em>{timeLabel(thread.lastMessageAtMs)}</em>
                </button>
              )) : (
                <div className="message-empty-state">
                  <strong>Konuşma yok</strong>
                  <span>Profil sayfasından mesaj göndererek başlatabilirsin.</span>
                </div>
              )}
            </aside>

            <div className="message-chat-panel">
              {selectedThread ? (
                <>
                  <div className="message-chat-person">
                    {profileAvatar(selectedThread.other)}
                    <div>
                      <strong>{selectedThread.other.displayName || "Kullanıcı"}</strong>
                      <span>@{selectedThread.other.nickname || selectedThread.other.uid}</span>
                      <small className="message-profile-meta">{[selectedThread.other.profileTitle, selectedThread.other.level ? `Level ${selectedThread.other.level}` : ""].filter(Boolean).join(" • ")}</small>
                    </div>
                  </div>
                  <div className="message-list">
                    {messages.map((message) => (
                      <div className={`message-bubble ${message.senderUid === user.uid ? "is-mine" : "is-theirs"}`} key={message.id}>
                        <p>{message.body}</p>
                        <span>{timeLabel(message.createdAtMs)}</span>
                      </div>
                    ))}
                    {!messages.length ? <div className="message-empty-state message-empty-state--chat"><strong>İlk mesajı yaz</strong><span>Gönderilen mesajlar silinemez ve geri alınamaz.</span></div> : null}
                    <div ref={messagesEndRef} />
                  </div>
                  <form className="message-compose" onSubmit={submitMessage}>
                    <textarea
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                      placeholder="Mesaj yaz..."
                      maxLength={1200}
                      rows={2}
                    />
                    <Button variant="primary" size="md" disabled={!draft.trim() || loadingTarget}>Gönder</Button>
                  </form>
                </>
              ) : (
                <div className="message-empty-state message-empty-state--wide">
                  <strong>Bir konuşma seç</strong>
                  <span>Son konuşmalar solda listelenir.</span>
                </div>
              )}
            </div>
          </div>

          {status.text ? <p className={`message-status message-status--${status.type || "info"}`}>{status.text}</p> : null}
          {loadingTarget ? <p className="message-status">Konuşma hazırlanıyor...</p> : null}
        </section>
      ) : null}
    </div>
  );
}
